import { Children, cloneElement, isValidElement, useId } from 'react';

/**
 * A labelled form field.
 *
 * Extracted from four byte-identical copies (WishFormModal, SuggestIdeaModal,
 * SurpriseGiftModal, WishesTab). They had already drifted nowhere, but four copies of
 * anything is four places to fix a bug — and this one had a bug.
 *
 * **The bug:** the old version rendered a bare `<label>` with no `htmlFor` that did not
 * wrap its control, so nothing in any of those forms was programmatically associated
 * with its label. A screen reader announced every input as unlabelled: "edit text",
 * with no idea whether it wanted a title, a price or a URL.
 *
 * **Why not simply wrap the children in the label.** Two fields wrap checkbox GROUPS
 * ("Share into groups", the recipient list) whose rows carry their own `<label>` each,
 * and nested labels are invalid HTML with undefined behaviour for assistive tech. A
 * wrapping label is also semantically wrong for a group: a label names ONE control.
 *
 * So the association depends on what is inside:
 *
 *  - **A single form control** → a generated id, `htmlFor` on the label. Clicking the
 *    label focuses the control, and it is announced by name.
 *  - **Anything else** (a group of checkboxes, a composite) → `<fieldset>` + `<legend>`,
 *    which is what names a GROUP of controls. Its default border and padding are reset
 *    so the visual result is unchanged.
 *
 * A control that already carries its own `id` keeps it — the caller wins.
 */
export default function Field({ label, required, children }) {
    const generatedId = useId();

    const only = Children.count(children) === 1 && isValidElement(children) ? children : null;
    const isSingleControl =
        only !== null && ['input', 'select', 'textarea'].includes(only.type);

    const labelText = (
        <>
            {label} {required && <span className="text-red-500">*</span>}
        </>
    );

    if (isSingleControl) {
        const id = only.props.id || generatedId;
        return (
            <div>
                <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
                    {labelText}
                </label>
                {cloneElement(only, { id })}
            </div>
        );
    }

    // A group, or a composite we can't identify a single control inside. <legend> names
    // the whole set, which is what assistive tech expects for a checkbox/radio group.
    return (
        <fieldset className="min-w-0 border-0 p-0 m-0">
            <legend className="block text-sm font-medium text-gray-700 mb-1 p-0">
                {labelText}
            </legend>
            {children}
        </fieldset>
    );
}
